import type { SearchResource } from "./searches";

// The search-progress read: an optional `progress` object riding
// GET /searches/:id (the read the wait already polls), served by the backend's
// SearchProgress. It carries only the wait/release decision; results,
// candidates, and the badge keep flowing through the search/attempt read path.
export type SearchProgress = {
  phase: "searching" | "resolved";
  // How much patience the wait should have, present while searching:
  // in_progress → hold up to the cap; backlogged/offline → release at the
  // minimum wait. Never surfaced to the user.
  signal: "in_progress" | "backlogged" | "offline" | null;
};

const PHASES: SearchProgress["phase"][] = ["searching", "resolved"];
const SIGNALS: Exclude<SearchProgress["signal"], null>[] = ["in_progress", "backlogged", "offline"];

const isPhase = (v: unknown): v is SearchProgress["phase"] =>
  typeof v === "string" && PHASES.some((p) => p === v);
const isSignal = (v: unknown): v is Exclude<SearchProgress["signal"], null> =>
  typeof v === "string" && SIGNALS.some((s) => s === v);

// Skew posture: the phase gates the read — an unrecognized shape is discarded
// whole, even one carrying a valid signal (deliberately: don't trust a shape
// we don't know) — and the signal is validated against its union; release
// itself additionally requires an exact backlogged/offline match in the
// machine, so nothing unrecognized can release the user early. The wire `v`
// stays informational. Absent or malformed progress synthesizes the
// pre-signal behavior (patience while the search is live, resolved once it
// isn't), never a broken wait.
export function progressFrom(search: SearchResource | undefined): SearchProgress {
  const wire = search && search.progress;
  if (wire && typeof wire === "object") {
    const { phase, signal } = wire as { phase?: unknown; signal?: unknown };
    if (isPhase(phase)) {
      return { phase, signal: isSignal(signal) ? signal : null };
    }
  }
  if (search && (search.status === "complete" || search.status === "failed")) {
    return { phase: "resolved", signal: null };
  }
  return { phase: "searching", signal: "in_progress" };
}
